import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { Close, Payment } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useRecordPayment } from '../../hooks/useInvoices';
import { Invoice } from '../../services/invoices';
import { getErrorInfo, ErrorInfo } from '../../services/api';
import CurrencyInput from '../common/CurrencyInput';
import ErrorMessage from '../ErrorMessage';

const recordPaymentSchema = z.object({
  amount: z.number().min(0.01, 'Amount must be greater than 0'),
  method: z.enum(['cash', 'check', 'bank_transfer', 'credit_card', 'paypal', 'venmo', 'zelle', 'stripe', 'other']),
  paidAt: z.date({ required_error: 'Payment date is required' }),
  reference: z.string().max(255).optional(),
  notes: z.string().max(500).optional(),
});

type RecordPaymentFormData = z.infer<typeof recordPaymentSchema>;

interface RecordPaymentModalProps {
  open: boolean;
  invoice: Invoice | null;
  onClose: () => void;
}

const paymentMethods = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'other', label: 'Other' },
];

export default function RecordPaymentModal({ open, invoice, onClose }: RecordPaymentModalProps) {
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const recordPayment = useRecordPayment();

  const amountDue = invoice?.amountDue || 0;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RecordPaymentFormData>({
    resolver: zodResolver(recordPaymentSchema),
    defaultValues: {
      amount: amountDue / 100,  // Convert cents to dollars
      method: 'bank_transfer',
      paidAt: new Date(),
      reference: '',
      notes: '',
    },
  });

  // Reset form when invoice changes
  useEffect(() => {
    if (invoice) {
      reset({
        amount: invoice.amountDue / 100,
        method: 'bank_transfer',
        paidAt: new Date(),
        reference: '',
        notes: '',
      });
    }
  }, [invoice, reset]);

  const onSubmit = async (data: RecordPaymentFormData) => {
    if (!invoice) return;
    setErrorInfo(null);

    try {
      await recordPayment.mutateAsync({
        id: invoice._id,
        data: {
          amount: Math.round(data.amount * 100),  // Convert to cents
          method: data.method,
          paidAt: data.paidAt.toISOString(),
          reference: data.reference,
          notes: data.notes,
        },
      });

      reset();
      onClose();
    } catch (err: unknown) {
      const info = getErrorInfo(err);
      setErrorInfo({
        ...info,
        endpoint: `/invoices/${invoice._id}/payments`,
        method: 'POST',
        payload: {
          amount: Math.round(data.amount * 100),
          method: data.method,
          paidAt: data.paidAt.toISOString(),
          reference: data.reference,
          notes: data.notes,
        },
      });
    }
  };

  const handleClose = () => {
    reset();
    setErrorInfo(null);
    onClose();
  };

  if (!invoice) return null;

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: '#DCFCE7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#10B981',
                }}
              >
                <Payment />
              </Box>
              <Typography variant="h6">Record Payment</Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            {errorInfo && (
              <ErrorMessage
                error={errorInfo}
                userFriendlyMessage="Unable to record payment. Please try again."
                onClose={() => setErrorInfo(null)}
              />
            )}

            {/* Invoice Info */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Invoice {invoice.invoiceNumber}
              </Typography>
              <Typography variant="h6">
                Amount Due: {formatCurrency(amountDue)}
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {/* Amount */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="amount"
                  control={control}
                  render={({ field }) => (
                    <CurrencyInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Amount"
                      fullWidth
                      required
                      error={!!errors.amount}
                      helperText={errors.amount?.message}
                    />
                  )}
                />
              </Grid>

              {/* Payment Method */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="method"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth required>
                      <InputLabel>Payment Method</InputLabel>
                      <Select {...field} label="Payment Method">
                        {paymentMethods.map((method) => (
                          <MenuItem key={method.value} value={method.value}>
                            {method.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Payment Date */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="paidAt"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Payment Date"
                      value={field.value}
                      onChange={(newValue) => field.onChange(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!errors.paidAt,
                          helperText: errors.paidAt?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Reference */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="reference"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Reference / Transaction ID"
                      fullWidth
                      placeholder="Check #, Transaction ID, etc."
                      error={!!errors.reference}
                      helperText={errors.reference?.message}
                    />
                  )}
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes"
                      multiline
                      rows={2}
                      fullWidth
                      placeholder="Additional payment notes..."
                      error={!!errors.notes}
                      helperText={errors.notes?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="success"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
}
